import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Dumbbell, CalendarPlus } from 'lucide-react';
import type { Workout } from '@techsofit/api-types';
import { Button, Card, Badge, Skeleton, EmptyState } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { ScheduleForm } from '../components/ScheduleForm';
import { useAsync } from '../lib/useAsync';
import { getWorkouts } from '../lib/gymApi';

/** List of reusable workouts; entry point to the builder. */
export function WorkoutsListPage() {
  const { data, loading } = useAsync(getWorkouts);
  const nav = useNavigate();
  const workouts = data ?? [];
  const [scheduling, setScheduling] = useState<Workout | null>(null);

  return (
    <section>
      <PageHeader
        eyebrow={data ? `${workouts.length} workouts` : undefined}
        title="Workouts"
        description="Reusable workouts you build from the library, then schedule to members."
        actions={
          <Button onClick={() => nav('/workouts/new')}>
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">Build workout</span>
          </Button>
        }
      />

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-card" />
          ))}
        </div>
      ) : workouts.length === 0 ? (
        <EmptyState
          icon={<Dumbbell className="size-6" aria-hidden />}
          title="No workouts yet"
          description="Build your first workout from the exercise library."
          action={<Button onClick={() => nav('/workouts/new')}>Build workout</Button>}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {workouts.map((w) => (
            <WorkoutCard
              key={w.id}
              workout={w}
              onOpen={() => nav(`/workouts/${w.id}/edit`)}
              onSchedule={() => setScheduling(w)}
            />
          ))}
        </div>
      )}

      {scheduling && (
        <ScheduleForm
          workoutId={scheduling.id}
          onClose={() => setScheduling(null)}
          onSaved={() => setScheduling(null)}
        />
      )}
    </section>
  );
}

function WorkoutCard({ workout, onOpen, onSchedule }: { workout: Workout; onOpen: () => void; onSchedule: () => void }) {
  const groups = Array.from(new Set(workout.items.flatMap((i) => i.muscleGroups))).slice(0, 4);
  return (
    <Card className="flex h-full flex-col p-5 transition-shadow hover:shadow-card-hover">
      <button type="button" onClick={onOpen} className="flex flex-1 flex-col text-left">
        <p className="font-display text-lg font-semibold text-ink">{workout.name}</p>
        <p className="mt-0.5 text-sm text-muted">
          {workout.itemCount} {workout.itemCount === 1 ? 'exercise' : 'exercises'}
        </p>
        {groups.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {groups.map((g) => (
              <Badge key={g}>{g}</Badge>
            ))}
          </div>
        )}
        {workout.notes && <p className="mt-3 line-clamp-2 text-sm text-ink-soft">{workout.notes}</p>}
      </button>
      <div className="mt-4 flex items-center gap-2 border-t border-line pt-3">
        <Button size="sm" variant="outline" onClick={onSchedule}>
          <CalendarPlus className="size-4" aria-hidden /> Schedule
        </Button>
        <Button size="sm" variant="ghost" onClick={onOpen}>
          View
        </Button>
      </div>
    </Card>
  );
}
